import { Buffer } from 'buffer';
// взято из https://github.com/steel1990/tga/blob/master/index.js
// doc: http://paulbourke.net/dataformats/tga/
const enableDebug = false;
// eslint-disable-next-line no-console
const debug = (...args: Parameters<typeof console.debug>) => enableDebug && console.debug(...args);

export type TGAHeader = {
  idlength: number;
  colourMapType: number;
  dataTypeCode: number;
  colourMapOrigin: number;
  colourMapLength: number;
  colourMapDepth: number;
  xOrigin: number;
  yOrigin: number;
  width: number;
  height: number;
  bitsPerPixel: number;
  imageDescriptor: number;
  bytesPerPixel: number;
};

export class TGA {
  buffer: Buffer;
  isFlipY: boolean;
  header: TGAHeader;
  pixels: Uint8Array;
  constructor(buffer: Buffer) {
    debug('constructor');
    this.buffer = buffer;
    debug('readHeader start');
    this.header = TGA.getHeader(this.buffer);
    this.isFlipY = !(this.header.imageDescriptor & 32);
    debug('readHeader result', this.header);
    debug('parse');
    if (!this.check()) {
      throw new Error('check failed');
      return;
    }
    debug('readPixels');
    const pixelCount = this.header.width * this.header.height;
    const data = new Uint8Array(this.buffer);
    this.pixels = new Uint8Array(pixelCount * 4);
    let offset = 18;

    for (let i = 0; i < pixelCount; i++) {
      if (this.header.dataTypeCode === 2) {
        this.addPixel(data, offset, i);
      } else if (this.header.dataTypeCode === 10) {
        const flag = data[offset++];
        const count = flag & 0x7f;
        const isRLEChunk = flag & 0x80;
        this.addPixel(data, offset, i);
        for (let j = 0; j < count; j++) {
          if (!isRLEChunk) {
            offset += this.header.bytesPerPixel;
          }
          this.addPixel(data, offset, ++i);
        }
      }
      offset += this.header.bytesPerPixel;
    }
  }
  static createTgaBuffer(width: number, height: number, pixels: Uint8Array, dontFlipY: boolean) {
    debug('createTgaBuffer');
    const buffer = Buffer.alloc(18 + pixels.length);
    // write header
    buffer.writeInt8(0, 0);
    buffer.writeInt8(0, 1);
    buffer.writeInt8(2, 2);
    buffer.writeInt16LE(0, 3);
    buffer.writeInt16LE(0, 5);
    buffer.writeInt8(0, 7);
    buffer.writeInt16LE(0, 8);
    buffer.writeInt16LE(0, 10);
    buffer.writeInt16LE(width, 12);
    buffer.writeInt16LE(height, 14);
    buffer.writeInt8(32, 16);
    buffer.writeInt8(0, 17);

    let offset = 18;
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        const idx = ((dontFlipY ? i : height - i - 1) * width + j) * 4;
        buffer.writeUInt8(pixels[idx + 2], offset++); // b
        buffer.writeUInt8(pixels[idx + 1], offset++); // g
        buffer.writeUInt8(pixels[idx], offset++); // r
        buffer.writeUInt8(pixels[idx + 3], offset++); // a
      }
    }

    return buffer;
  }
  static getHeader(buffer: Buffer) {
    debug('getHeader');
    const idlength = buffer.readInt8(0);
    const colourMapType = buffer.readInt8(1);
    const dataTypeCode = buffer.readInt8(2);
    const colourMapOrigin = buffer.readInt16LE(3);
    const colourMapLength = buffer.readInt16LE(5);
    const colourMapDepth = buffer.readInt8(7);
    const xOrigin = buffer.readInt16LE(8);
    const yOrigin = buffer.readInt16LE(10);
    const width = buffer.readInt16LE(12);
    const height = buffer.readInt16LE(14);
    const bitsPerPixel = buffer.readInt8(16);
    const imageDescriptor = buffer.readInt8(17);

    const header: TGAHeader = {
      idlength,
      colourMapType,
      dataTypeCode,
      colourMapOrigin,
      colourMapLength,
      colourMapDepth,
      xOrigin,
      yOrigin,
      width,
      height,
      bitsPerPixel,
      bytesPerPixel: bitsPerPixel / 8,
      imageDescriptor,
    };
    debug('getHeader', header);
    return header;
  }

  check() {
    debug('check tga file');
    const header = this.header;
    /* What can we handle */
    if (header.dataTypeCode !== 2 && header.dataTypeCode !== 10) {
      // eslint-disable-next-line no-console
      console.error('Can only handle image type 2 and 10');
      return false;
    }
    if (header.bitsPerPixel !== 16 && header.bitsPerPixel !== 24 && header.bitsPerPixel !== 32) {
      // eslint-disable-next-line no-console
      console.error('Can only handle pixel depths of 16, 24, and 32');
      return false;
    }
    if (header.colourMapType !== 0 && header.colourMapType !== 1) {
      // eslint-disable-next-line no-console
      console.error('Can only handle colour map types of 0 and 1');
      return false;
    }
    return true;
  }
  addPixel(arr: Uint8Array, offset: number, idx: number) {
    if (this.isFlipY) {
      const y = this.header.height - 1 - Math.floor(idx / this.header.width);
      idx = y * this.header.width + (idx % this.header.width);
    }
    idx *= 4;
    const count = this.header.bytesPerPixel;
    let r = 255;
    let g = 255;
    let b = 255;
    let a = 255;
    if (count === 3 || count === 4) {
      r = arr[offset + 2];
      g = arr[offset + 1];
      b = arr[offset];
      a = count === 4 ? arr[offset + 3] : 255;
    } else if (count === 2) {
      r = (arr[offset + 1] & 0x7c) << 1;
      g = ((arr[offset + 1] & 0x03) << 6) | ((arr[offset] & 0xe0) >> 2);
      b = (arr[offset] & 0x1f) << 3;
      a = arr[offset + 1] & 0x80;
    } else {
      // eslint-disable-next-line no-console
      console.error('cant transform to Pixel');
    }

    this.pixels[idx] = r;
    this.pixels[idx + 1] = g;
    this.pixels[idx + 2] = b;
    this.pixels[idx + 3] = a;
  }
}
